import React, { useState, useEffect } from 'react'
import { Image, RefreshControl } from 'react-native'
import {
  Receipt, Package, Wallet, Clock, ChevronRight,
  CreditCard, Banknote, Smartphone, ArrowLeft,
} from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import {
  Spinner, Text, XStack, YStack, ScrollView,
} from '@fliisi/pollen'
import { FullScreenLayout } from '../components/ScreenLayout'

const merchantColors = { primary: '#C07A5F', primaryLight: '#D4A088', accent: '#748C70' }

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MerchantActivity'>
  route: { params: { storeId: string; tab?: 'sales' | 'offers' | 'payouts' } }
}

type TabType = 'sales' | 'offers' | 'payouts'

interface Sale {
  id: string; product_id: string; amount: number; quantity: number; payment_method: string; created_at: string
  product?: { title: string; title_fi?: string; image_url?: string }
}
interface Offer {
  id: string; status: 'pending' | 'accepted' | 'rejected' | 'completed'; created_at: string; seller_notes?: string; total_price: number
  seller: { id: string; email: string; display_name?: string }; items: { title: string; price: number }[]
}
interface Payout {
  id: string; seller_id: string; amount: number; status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string; completed_at?: string; seller?: { email: string; display_name?: string }
}

export default function MerchantActivityScreen({ navigation, route }: Props) {
  const { storeId, tab: initialTab } = route.params
  const { t, language } = useLanguage()
  const [activeTab, setActiveTab] = useState<TabType>(initialTab || 'sales')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [storeName, setStoreName] = useState('')
  const [sales, setSales] = useState<Sale[]>([])
  const [offers, setOffers] = useState<Offer[]>([])
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [pendingOffersCount, setPendingOffersCount] = useState(0)

  useEffect(() => { loadStoreInfo(); loadData() }, [storeId])
  useEffect(() => { loadData() }, [activeTab])

  const loadStoreInfo = async () => {
    const { data } = await supabase.from('flea_markets').select('name').eq('id', storeId).single()
    if (data) setStoreName(data.name)
  }

  const loadData = async () => {
    setLoading(true)
    try {
      switch (activeTab) { case 'sales': await loadSales(); break; case 'offers': await loadOffers(); break; case 'payouts': await loadPayouts(); break }
    } finally { setLoading(false); setRefreshing(false) }
  }

  const loadSales = async () => {
    const { data, error } = await supabase.from('products')
      .select('id, title, title_fi, price, sold_at, status, product_images!product_images_product_id_fkey(image_url, is_primary)')
      .eq('flea_market_id', storeId).eq('status', 'sold').order('sold_at', { ascending: false }).limit(50)
    if (!error && data) {
      setSales(data.map(p => {
        const primaryImage = (p.product_images as any[])?.find(img => img.is_primary) || (p.product_images as any[])?.[0]
        return { id: p.id, product_id: p.id, amount: p.price, quantity: 1, payment_method: 'unknown', created_at: p.sold_at || new Date().toISOString(), product: { title: p.title, title_fi: p.title_fi, image_url: primaryImage?.image_url } }
      }))
    }
  }

  const loadOffers = async () => {
    const { data, error } = await supabase.from('seller_offers')
      .select('id, status, created_at, seller_notes, total_price, seller:users!seller_offers_seller_id_fkey(id, email, display_name), items:offer_items(title, price)')
      .eq('store_id', storeId).order('created_at', { ascending: false }).limit(50)
    if (!error && data) {
      const offersData = data.map(o => ({ ...o, seller: o.seller as any, items: o.items as any[] || [] }))
      setOffers(offersData); setPendingOffersCount(offersData.filter(o => o.status === 'pending').length)
    } else setOffers([])
  }

  const loadPayouts = async () => {
    const { data, error } = await supabase.from('payouts')
      .select('id, seller_id, amount, status, created_at, completed_at, seller:users!payouts_seller_id_fkey(email, display_name)')
      .eq('flea_market_id', storeId).order('created_at', { ascending: false }).limit(50)
    if (!error && data) setPayouts(data.map(p => ({ ...p, seller: p.seller as any })))
    else setPayouts([])
  }

  const getPaymentIcon = (method: string) => {
    switch (method) { case 'card': return CreditCard; case 'cash': return Banknote; case 'mobilepay': return Smartphone; default: return Receipt }
  }
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  const getStatusColor = (status: string) => {
    switch (status) { case 'completed': case 'accepted': return '#4CAF50'; case 'pending': return '#FF9800'; case 'processing': return '#2196F3'; case 'rejected': case 'failed': return '#EF4444'; default: return '#999' }
  }

  const tabs: { key: TabType; label: string; icon: React.ComponentType<any>; badge?: number }[] = [
    { key: 'sales', label: t('sales') || 'Sales', icon: Receipt },
    { key: 'offers', label: t('offers') || 'Offers', icon: Package, badge: pendingOffersCount },
    { key: 'payouts', label: t('payouts') || 'Payouts', icon: Wallet },
  ]

  const renderEmpty = (Icon: React.ComponentType<any>, title: string, desc: string) => (
    <YStack f={1} jc="center" ai="center" p="$xl" mt={60}><Icon size={48} color="#999" /><Text fontSize={18} fontWeight="600" color="$color" mt="$md">{title}</Text><Text fontSize={14} color="$colorSecondary" ta="center" mt="$sm">{desc}</Text></YStack>
  )

  const renderSales = () => (
    <ScrollView f={1} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData() }} />}>
      {sales.length === 0 ? renderEmpty(Receipt, t('no_sales_yet') || 'No sales yet', t('sales_will_appear_here') || 'Sales will appear here') : (
        <>
          <YStack bg="$backgroundCard" m="$md" p="$md" br="$lg" elevation={1}>
            <Text fontSize={14} fontWeight="600" color="$colorSecondary" mb="$sm">{t('today') || 'Today'}</Text>
            <XStack gap="$lg">
              <YStack f={1}>
                <Text fontSize={24} fontWeight="bold" color={merchantColors.primary}>{sales.filter(s => new Date(s.created_at).toDateString() === new Date().toDateString()).length}</Text>
                <Text fontSize={12} color="$colorSecondary">{t('sales') || 'Sales'}</Text>
              </YStack>
              <YStack f={1}>
                <Text fontSize={24} fontWeight="bold" color={merchantColors.primary}>{sales.filter(s => new Date(s.created_at).toDateString() === new Date().toDateString()).reduce((sum, s) => sum + s.amount, 0).toFixed(2)} €</Text>
                <Text fontSize={12} color="$colorSecondary">{t('revenue') || 'Revenue'}</Text>
              </YStack>
            </XStack>
          </YStack>
          {sales.map(sale => {
            const PaymentIcon = getPaymentIcon(sale.payment_method)
            return (
              <XStack key={sale.id} ai="center" bg="$backgroundCard" mx="$md" mb="$sm" p="$md" br="$md" gap="$sm">
                {sale.product?.image_url ? <Image source={{ uri: sale.product.image_url }} style={{ width: 48, height: 48, borderRadius: 8 }} />
                  : <YStack w={48} h={48} br="$md" bg="$backgroundSecondary" jc="center" ai="center"><Package size={20} color="#999" /></YStack>}
                <YStack f={1}>
                  <Text fontSize={14} fontWeight="500" color="$color" numberOfLines={1}>{language === 'fi' && sale.product?.title_fi ? sale.product.title_fi : sale.product?.title}</Text>
                  <XStack ai="center" gap={4} mt={4}><Clock size={12} color="#999" /><Text fontSize={12} color="$colorSecondary">{formatDate(sale.created_at)}</Text></XStack>
                </YStack>
                <YStack ai="flex-end" gap={4}>
                  <Text fontSize={16} fontWeight="600" color={merchantColors.primary}>{sale.amount.toFixed(2)} €</Text>
                  <PaymentIcon size={14} color="#999" />
                </YStack>
              </XStack>
            )
          })}
        </>
      )}
    </ScrollView>
  )

  const renderOffers = () => (
    <ScrollView f={1} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData() }} />}>
      {offers.length === 0 ? renderEmpty(Package, t('no_offers_yet') || 'No offers yet', t('offers_will_appear_here') || 'Incoming offers will appear here') : offers.map(offer => (
        <YStack key={offer.id} bg="$backgroundCard" mx="$md" mb="$sm" p="$md" br="$lg" elevation={1}
          onPress={() => navigation.navigate('IncomingOffers', { storeId })} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <XStack jc="space-between" ai="center" mb="$sm">
            <YStack px="$sm" py={4} br="$full" bg={`${getStatusColor(offer.status)}20`}><Text fontSize={12} fontWeight="600" color={getStatusColor(offer.status)} textTransform="capitalize">{t(offer.status) || offer.status}</Text></YStack>
            <Text fontSize={12} color="$colorSecondary">{formatDate(offer.created_at)}</Text>
          </XStack>
          <YStack mb="$sm">
            <Text fontSize={15} fontWeight="600" color="$color">{offer.seller?.display_name || offer.seller?.email || 'Unknown'}</Text>
            <Text fontSize={13} color="$colorSecondary" mt={2}>{offer.items.length} {offer.items.length === 1 ? t('item') : t('items')}</Text>
          </YStack>
          {offer.items.length > 0 && (
            <XStack gap="$sm" mb="$sm" flexWrap="wrap">
              {offer.items.slice(0, 4).map((item, idx) => (
                <YStack key={idx} w={48} h={48} br="$md" bg="$backgroundSecondary" jc="center" ai="center"><Package size={16} color="#999" /></YStack>
              ))}
              {offer.items.length > 4 && <YStack w={48} h={48} br="$md" bg="$backgroundSecondary" jc="center" ai="center"><Text fontSize={14} fontWeight="600" color="$colorSecondary">+{offer.items.length - 4}</Text></YStack>}
            </XStack>
          )}
          <XStack jc="space-between" ai="center" pt="$sm" btw={1} btc="$borderColor">
            <Text fontSize={18} fontWeight="bold" color={merchantColors.primary}>{offer.total_price?.toFixed(2) || '0.00'} €</Text>
            <ChevronRight size={20} color={merchantColors.primary} />
          </XStack>
        </YStack>
      ))}
    </ScrollView>
  )

  const renderPayouts = () => (
    <ScrollView f={1} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData() }} />}>
      {payouts.length === 0 ? renderEmpty(Wallet, t('no_payouts_yet') || 'No payouts yet', t('payouts_will_appear_here') || 'Seller payouts will appear here') : payouts.map(payout => (
        <XStack key={payout.id} ai="center" bg="$backgroundCard" mx="$md" mb="$sm" p="$md" br="$md" gap="$sm">
          <YStack w={40} h={40} br="$full" bg={`${merchantColors.primary}15`} jc="center" ai="center"><Wallet size={20} color={merchantColors.primary} /></YStack>
          <YStack f={1}>
            <Text fontSize={14} fontWeight="500" color="$color">{payout.seller?.display_name || payout.seller?.email || 'Unknown'}</Text>
            <Text fontSize={12} color="$colorSecondary" mt={2}>{formatDate(payout.created_at)}</Text>
          </YStack>
          <YStack ai="flex-end" gap={4}>
            <Text fontSize={16} fontWeight="600" color={merchantColors.primary}>{payout.amount.toFixed(2)} €</Text>
            <YStack px="$sm" py={4} br="$full" bg={`${getStatusColor(payout.status)}20`}><Text fontSize={11} fontWeight="600" color={getStatusColor(payout.status)}>{t(payout.status) || payout.status}</Text></YStack>
          </YStack>
        </XStack>
      ))}
    </ScrollView>
  )

  return (
    <FullScreenLayout>
      {/* Header */}
      <XStack ai="center" jc="space-between" p="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <YStack p="$sm" onPress={() => navigation.navigate('MerchantDashboard', { storeId })} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
          <ArrowLeft size={20} color={merchantColors.primary} />
        </YStack>
        <Text fontSize={20} fontWeight="bold" color="$color">{t('activity') || 'Activity'}</Text>
        <YStack w={36} />
      </XStack>

      {/* Tab Bar */}
      <XStack bg="$backgroundCard" bbw={1} bbc="$borderColor">
        {tabs.map(tab => {
          const Icon = tab.icon; const isActive = activeTab === tab.key
          return (
            <YStack key={tab.key} f={1} py="$sm" ai="center" bbw={2} bbc={isActive ? merchantColors.primary : 'transparent'}
              onPress={() => setActiveTab(tab.key)} pressStyle={{ opacity: 0.8 }} animation="quick" cursor="pointer">
              <XStack ai="center" gap={6}>
                <Icon size={18} color={isActive ? merchantColors.primary : '#999'} />
                <Text fontSize={14} fontWeight={isActive ? '600' : '500'} color={isActive ? merchantColors.primary : '$colorSecondary'}>{tab.label}</Text>
                {tab.badge && tab.badge > 0 && <YStack bg="#EF4444" br={10} minWidth={18} h={18} jc="center" ai="center" px={4}><Text color="white" fontSize={10} fontWeight="700">{tab.badge}</Text></YStack>}
              </XStack>
            </YStack>
          )
        })}
      </XStack>

      {loading ? <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack> : (
        <>{activeTab === 'sales' && renderSales()}{activeTab === 'offers' && renderOffers()}{activeTab === 'payouts' && renderPayouts()}</>
      )}
    </FullScreenLayout>
  )
}
